import { Tab as BaseTab } from "@headlessui/react";
import { Body, Headline } from "~/ui-library";
import { cva, cx } from "cva";
import { useState } from "react";
import { OptionData, Select, Option } from "~/ui-library/components/Select";
import { useActiveBreakpoint } from "~/lib/client/useActiveBreakpoint";

export type TabbedChartTab = {
  label: string;
  tabContent: React.ReactNode;
};

export type TabbedChartsProps = {
  /** the max number of tabs on SMALL breakpoint to display before showing the select */
  maxMobileTabs?: number;
  /** the max number of tabs on MEDIUM+ breakpoints to display before showing the select */
  maxDesktopTabs?: number;
  options: OptionData[];
  tabs: TabbedChartTab[];
  headline?: string;
  subhead?: string;
  title?: string;
  blockId?: string;
};

export type ChartTabProps = {
  onChange: (selected: number) => void;
  selectedIndex: number;
  showTabs: boolean;
  tabs: TabbedChartsProps["tabs"];
};

const showControlStyles = cva("", {
  variants: {
    showControl: {
      true: "",
      false: "hidden",
    },
  },
});

const TabbedStyles = cx(
  "py-xxs px-s",
  "border-r-1 border-gray-300 last:border-none",
  "w-fit",
  "ui-selected:text-black ui-selected:font-semibold ui-selected:bg-primary-100",
  "ui-not-selected:text-gray-700"
);

const ChartTabs: React.FC<ChartTabProps> = ({
  selectedIndex,
  showTabs,
  tabs,
  onChange,
}) => (
  <BaseTab.Group onChange={onChange} selectedIndex={selectedIndex}>
    <div className="flex justify-center" id="chartTab">
      <BaseTab.List
        className={cx(
          showControlStyles({ showControl: showTabs }),
          "w-fit rounded-3 border-1 border-gray-300"
        )}
        data-testid="chart-tabs"
      >
        {tabs.map((tab, idx) => (
          <BaseTab className={TabbedStyles} key={idx}>
            {tab.label}
          </BaseTab>
        ))}
      </BaseTab.List>
    </div>
    <BaseTab.Panels>
      {tabs.map((tab, idx) => (
        <BaseTab.Panel key={idx}>{tab.tabContent}</BaseTab.Panel>
      ))}
    </BaseTab.Panels>
  </BaseTab.Group>
);

const DEFAULT_MAX_MOBILE_TABS = 1;
const DEFAULT_MAX_DESKTOP_TABS = 5;

export const TabbedCharts: React.FC<TabbedChartsProps> = ({
  maxDesktopTabs = DEFAULT_MAX_DESKTOP_TABS,
  maxMobileTabs = DEFAULT_MAX_MOBILE_TABS,
  headline,
  subhead,
  title,
  options,
  tabs,
  blockId,
}) => {
  const isMobile = !useActiveBreakpoint("MEDIUM");
  const showSelect = isMobile
    ? tabs.length > maxMobileTabs
    : tabs.length > maxDesktopTabs;

  const [selected, setSelected] = useState(0);
  const selectedOption = options[selected];

  return (
    <div className="flex flex-col gap-m">
      <div className="flex flex-col">
        {headline ? (
          <div className="border-t-1 border-t-gray-300 pt-s">
            <Headline className="mb-s scroll-mt-xxl" level={3} id={blockId}>
              {headline}
            </Headline>
          </div>
        ) : null}
        {subhead ? (
          <Body size="large" className="pb-[8px]">
            {subhead}
          </Body>
        ) : null}
        {title ? <Body bold>{title}</Body> : null}
      </div>

      <div
        className={cx(
          showControlStyles({ showControl: showSelect }),
          "mx-auto w-full max-w-[400px]"
        )}
        data-testid="chart-select"
      >
        <Select
          selected={selectedOption}
          onChange={(value) => {
            setSelected(value.index!);
          }}
        >
          {options.map((value, idx) => (
            <Option key={`option-${idx}`} value={value}>
              {value.label}
            </Option>
          ))}
        </Select>
      </div>

      <ChartTabs
        onChange={setSelected}
        selectedIndex={selected}
        showTabs={!showSelect}
        tabs={tabs}
      />
    </div>
  );
};
